<script setup lang="ts">
import DSContainer from '~/components/ds/Container.vue'
import type { ShopProduct, ShopProductQuery, AccessoryCategory, ShopCategory } from '~/composables/useShopAPI'
import { SITE_URL, buildBreadcrumbSchema, serializeJsonLd } from '~/utils/schemaOrg'

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()
const { fetchProducts, fetchDeviceCategories, fetchAccessoryCategories } = useShopAPI()
const loading = ref(false)
const productLoadError = ref<string | null>(null)
const categoriesError = ref<string | null>(null)
const { translateTitle } = useShopI18n()

const shopTrustBadges = computed(() => [
  { icon: 'i-lucide-shield-check', label: t('shopPage.trustBadges.warranty') },
  { icon: 'i-lucide-truck', label: t('shopPage.trustBadges.shippingPolicy') },
  { icon: 'i-lucide-rotate-ccw', label: t('shopPage.trustBadges.returns') },
])

definePageMeta({
  layout: 'default'
})

// Product categories — loaded from backend (admin-managed)
const categories = ref<ShopCategory[]>([])
const DEFAULT_CATEGORY_ICON = 'i-lucide-smartphone'

// Virtual "Android" group: collapses all non-Apple categories into one filter row.
// Selecting it sends `categoryCodes` (CSV) instead of single `categoryCode`.
// Internal token stays `__android` to avoid colliding with a real category code;
// URLs expose the cleaner `android` (see syncQueryToUrl / onMounted hydration).
const ANDROID_GROUP_CODE = '__android'
const ANDROID_URL_TOKEN = 'android'
const APPLE_BRAND_CODE = 'apple'

function isAppleCategory(c: ShopCategory): boolean {
  return (c.brandCode || '').toLowerCase() === APPLE_BRAND_CODE
}

interface DisplayCategory {
  id: string
  code: string
  name: string
  icon: string | null
  nameTranslations?: Record<string, string> | null
  /** When set, this is the virtual Android group; selecting it queries with these codes. */
  groupedCodes?: string[]
}

// Apple categories preserve their backend sort order; Android group is appended last.
const displayCategories = computed<DisplayCategory[]>(() => {
  const apple: DisplayCategory[] = []
  const androidCodes: string[] = []
  for (const c of categories.value) {
    if (isAppleCategory(c)) {
      apple.push({ id: c.id, code: c.code, name: c.name, icon: c.icon, nameTranslations: c.nameTranslations })
    } else {
      androidCodes.push(c.code)
    }
  }
  const result: DisplayCategory[] = [...apple]
  if (androidCodes.length > 0) {
    result.push({
      id: ANDROID_GROUP_CODE,
      code: ANDROID_GROUP_CODE,
      name: t('shopPage.filters.androidGroup'),
      icon: DEFAULT_CATEGORY_ICON,
      groupedCodes: androidCodes,
    })
  }
  return result
})

function groupedCodesFor(code: string): string[] | null {
  const entry = displayCategories.value.find(c => c.code === code)
  return entry?.groupedCodes ?? null
}

// 7-level condition filters
const conditions = computed(() => [
  { id: 'sealed', labelKey: 'shopPage.conditions.sealed', descKey: 'shopPage.conditions.sealedDesc' },
  { id: 'like_new', labelKey: 'shopPage.conditions.like_new', descKey: 'shopPage.conditions.like_newDesc' },
  { id: 'excellent', labelKey: 'shopPage.conditions.excellent', descKey: 'shopPage.conditions.excellentDesc' },
  { id: 'very_good', labelKey: 'shopPage.conditions.very_good', descKey: 'shopPage.conditions.very_goodDesc' },
  { id: 'good', labelKey: 'shopPage.conditions.good', descKey: 'shopPage.conditions.goodDesc' },
  { id: 'fair', labelKey: 'shopPage.conditions.fair', descKey: 'shopPage.conditions.fairDesc' },
  { id: 'acceptable', labelKey: 'shopPage.conditions.acceptable', descKey: 'shopPage.conditions.acceptableDesc' }
])

// Product type ("Dispositivos" vs "Accesorios"). Devices keep all existing
// behavior; accessories swap the condition-grade filter for an accessory-category
// filter and drop the device-only query params.
type ProductType = 'DEVICE' | 'ACCESSORY'
const productType = ref<ProductType>('DEVICE')
const isAccessoryMode = computed(() => productType.value === 'ACCESSORY')

// Accessory categories — SSR-loaded for stable landing-page metadata, with a
// client retry when a visitor switches modes after hydration.
const accessoryCategories = ref<AccessoryCategory[]>([])
const accessoryCategoriesError = ref(false)
const selectedAccessoryCategory = ref<string | null>(null)

// Reactive filter state
const selectedCategory = ref<string | null>(null)
const selectedCondition = ref<string | null>(null)
const selectedConditionOption = computed(() =>
  conditions.value.find(condition => condition.id === selectedCondition.value) ?? null,
)
const priceRange = ref<[number | null, number | null]>([null, null])
const searchQuery = ref('')
const currentPage = ref(0)

// Mobile drawer edits a draft so browsing options does not refetch or mutate the grid.
const draftProductType = ref<ProductType>('DEVICE')
const draftCategory = ref<string | null>(null)
const draftCondition = ref<string | null>(null)
const draftAccessoryCategory = ref<string | null>(null)
const draftPriceRange = ref<[number | null, number | null]>([null, null])

// Sort options
const sortOptions = computed(() => [
  { label: t('shopPage.sort.newest'), value: 'newest' },
  { label: t('shopPage.sort.priceAsc'), value: 'price_asc' },
  { label: t('shopPage.sort.priceDesc'), value: 'price_desc' }
])
const selectedSort = ref('newest')

// Mobile filter drawer
const showFilters = ref(false)
const filterDialog = ref<HTMLElement | null>(null)
const filterTrigger = ref<HTMLButtonElement | null>(null)
let previousBodyOverflow = ''

// Products data from API
const products = ref<ShopProduct[]>([])
const totalProducts = ref(0)
const totalPages = ref(0)

// Build query from current filter state
const currentQuery = computed<ShopProductQuery>(() => {
  const query: ShopProductQuery = {
    page: currentPage.value,
    size: 12,
  }
  if (isAccessoryMode.value) {
    query.productType = 'ACCESSORY'
    if (selectedAccessoryCategory.value) query.accessoryCategoryCode = selectedAccessoryCategory.value
  } else {
    query.productType = 'DEVICE'
    if (selectedCategory.value) {
      const groupedCodes = groupedCodesFor(selectedCategory.value)
      if (groupedCodes && groupedCodes.length > 0) {
        query.categoryCodes = groupedCodes.join(',')
      } else {
        query.categoryCode = selectedCategory.value
      }
    }
    if (selectedCondition.value) query.conditionGrade = selectedCondition.value
  }

  // Price range with min ≤ max validation
  let min = priceRange.value[0]
  let max = priceRange.value[1]
  if (min !== null && max !== null && min > max) {
    [min, max] = [max, min]
  }
  if (min !== null && min > 0) query.minPrice = min
  if (max !== null && max > 0) query.maxPrice = max

  if (selectedSort.value !== 'newest') query.sort = selectedSort.value
  if (searchQuery.value.trim()) query.q = searchQuery.value.trim()
  return query
})

// Initial filter state from the URL — read synchronously so it is correct
// during SSR (was previously read in onMounted, leaving the first render blank).
const initialQ = route.query.q
if (typeof initialQ === 'string') searchQuery.value = initialQ
const initialType = route.query.type
if (initialType === 'accessory') productType.value = 'ACCESSORY'
const initialAccessoryCategory = route.query.accessory
if (typeof initialAccessoryCategory === 'string') selectedAccessoryCategory.value = initialAccessoryCategory
const initialCategory = route.query.category
if (typeof initialCategory === 'string') {
  selectedCategory.value = initialCategory === ANDROID_URL_TOKEN ? ANDROID_GROUP_CODE : initialCategory
}
// 其余列表状态也从 URL 恢复（必须在下方 watch 注册前读取，否则 watch 会把 page 重置为 0）。
const initialCondition = route.query.condition
if (typeof initialCondition === 'string' && conditions.value.some(condition => condition.id === initialCondition)) {
  selectedCondition.value = initialCondition
}
const initialSort = route.query.sort
const SORT_VALUES = ['newest', 'price_asc', 'price_desc'] as const
if (typeof initialSort === 'string' && SORT_VALUES.includes(initialSort as typeof SORT_VALUES[number])) selectedSort.value = initialSort
const initialMinPrice = route.query.minPrice
if (typeof initialMinPrice === 'string' && Number(initialMinPrice) > 0) {
  priceRange.value[0] = Number(initialMinPrice)
}
const initialMaxPrice = route.query.maxPrice
if (typeof initialMaxPrice === 'string' && Number(initialMaxPrice) > 0) {
  priceRange.value[1] = Number(initialMaxPrice)
}
normalizePriceRange(priceRange.value)
const initialPage = route.query.page
if (typeof initialPage === 'string' && /^\d+$/.test(initialPage)) {
  const p = Number(initialPage)
  if (p > 1) currentPage.value = p - 1 // URL 1-based → 内部 0-based
}

// Fetch products via useAsyncData so the grid is server-rendered. An empty
// client-only grid was being flagged as a soft 404 / thin page by Google,
// which dropped /shop and its stable category landing URLs from the index.
// Categories are required to resolve the virtual Android group during SSR.
const { data: categoryData, error: categoryFetchError } = await useAsyncData(
  'shop-categories',
  () => fetchDeviceCategories(),
)
categories.value = categoryData.value ?? []
categoriesError.value = categoryFetchError.value?.message ?? null

const { data: initialAccessoryCategories } = await useAsyncData(
  'shop-accessory-categories',
  () => isAccessoryMode.value ? fetchAccessoryCategories() : Promise.resolve([] as AccessoryCategory[]),
)
accessoryCategories.value = initialAccessoryCategories.value ?? []

const selectedLandingName = computed(() => {
  if (isAccessoryMode.value) {
    const selected = accessoryCategories.value.find(category => category.code === selectedAccessoryCategory.value)
    return selected
      ? translateTitle(selected.nameTranslations, selected.name)
      : t('shopPage.filters.accessories') as string
  }
  const selected = displayCategories.value.find(category => category.code === selectedCategory.value)
  return selected ? translateTitle(selected.nameTranslations, selected.name) : null
})
const isStableLanding = computed(() => {
  if (!isAccessoryMode.value) return selectedLandingName.value != null
  if (!selectedAccessoryCategory.value) return true
  return accessoryCategories.value.some(category => category.code === selectedAccessoryCategory.value)
})

// Keep known category landing pages self-canonical while collapsing unknown
// values and volatile facets (search, price, condition, sort and page) to /shop.
const canonicalShopQuery = computed<Record<string, string | undefined>>(() => {
  if (!isStableLanding.value) return {}
  if (isAccessoryMode.value) {
    return {
      type: 'accessory',
      accessory: selectedAccessoryCategory.value || undefined,
    }
  }
  return { category: selectedCategory.value === ANDROID_GROUP_CODE ? ANDROID_URL_TOKEN : selectedCategory.value || undefined }
})
useSeoCanonical('/shop', { canonicalQuery: canonicalShopQuery })
const landingTitle = computed(() => isStableLanding.value
  ? t('shopPage.landing.title', { category: selectedLandingName.value }) as string
  : t('shopPage.title') as string)
const landingDescription = computed(() => isStableLanding.value
  ? t('shopPage.landing.description', { category: selectedLandingName.value }) as string
  : t('shopPage.subtitle') as string)
const seoTitle = computed(() => isStableLanding.value
  ? t('shopPage.landing.seoTitle', { category: selectedLandingName.value }) as string
  : t('seo.shop.title') as string)
const seoDescription = computed(() => isStableLanding.value
  ? t('shopPage.landing.seoDescription', { category: selectedLandingName.value }) as string
  : t('seo.shop.description') as string)

useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  ogTitle: seoTitle,
  ogDescription: seoDescription,
})

const shopBreadcrumbSchema = computed(() => {
  const items = [
    { name: t('shopPage.breadcrumb.home') as string, url: `${SITE_URL}${localePath('/')}` },
    { name: t('shopPage.breadcrumb.shop') as string, url: `${SITE_URL}${localePath('/shop')}` },
  ]
  if (isStableLanding.value && selectedLandingName.value) {
    const query = isAccessoryMode.value
      ? `?type=accessory${selectedAccessoryCategory.value ? `&accessory=${encodeURIComponent(selectedAccessoryCategory.value)}` : ''}`
      : `?category=${selectedCategory.value === ANDROID_GROUP_CODE ? ANDROID_URL_TOKEN : encodeURIComponent(selectedCategory.value || '')}`
    items.push({ name: selectedLandingName.value, url: `${SITE_URL}${localePath('/shop')}${query}` })
  }
  return buildBreadcrumbSchema(items)
})

useHead({
  script: () => [{
    type: 'application/ld+json',
    innerHTML: serializeJsonLd(shopBreadcrumbSchema.value),
  }],
})

const { data: productData, error: initialProductsError } = await useAsyncData(
  'shop-products',
  () => fetchProducts(currentQuery.value),
)

watchEffect(() => {
  products.value = productData.value?.items ?? []
  totalProducts.value = productData.value?.total ?? 0
  totalPages.value = productData.value?.totalPages ?? 0
})

// Explicitly (re)fetch products for the current filter state and assign the grid.
// Client-side filter / sort / pagination / search changes drive this DIRECTLY rather
// than relying on useAsyncData's reactive watch — that watch was unreliable alongside
// the manual onMounted fallback (prod SSR can't reach the backend), so the Apply button
// and filter selections could appear to do nothing. This guarantees every action fetches.
async function loadProducts() {
  const requestId = ++latestProductsRequest
  loading.value = true
  try {
    productLoadError.value = null
    const result = await fetchProducts(currentQuery.value)
    if (requestId !== latestProductsRequest) return
    initialProductsError.value = null
    if (result.totalPages > 0 && currentPage.value >= result.totalPages) {
      currentPage.value = result.totalPages - 1
      syncQueryToUrl()
      void loadProducts()
      return
    }
    products.value = result.items
    totalProducts.value = result.total
    totalPages.value = result.totalPages
  } catch (e) {
    if (requestId !== latestProductsRequest) return
    productLoadError.value = e instanceof Error ? e.message : 'Failed to load products'
    // Keep the current grid; the next filter action will retry.
  } finally {
    if (requestId === latestProductsRequest) loading.value = false
  }
}

let latestProductsRequest = 0
const productsError = computed(() => productLoadError.value || initialProductsError.value?.message || null)

// Watch filters — reset page + sync URL + refetch
watch([selectedCategory, selectedCondition, selectedAccessoryCategory, selectedSort], () => {
  if (applyingRouteQuery) return
  currentPage.value = 0
  syncQueryToUrl()
  loadProducts()
})

// Sync the search box with the URL `q` param. The header search bar navigates to
// `/shop?q=...`; when we're already on /shop the page component is reused (only the
// query changes), so setup() doesn't re-run and the search would otherwise be ignored.
// Guarded against our own syncQueryToUrl() write — when q already matches, do nothing.
// Switch between devices and accessories. Clears the filters that don't apply to
// the other type so the (now hidden) state can't silently constrain the query.
function selectProductType(type: ProductType) {
  if (productType.value === type) return
  productType.value = type
  if (type === 'ACCESSORY') {
    selectedCategory.value = null
    selectedCondition.value = null
    loadAccessoryCategories()
  } else {
    selectedAccessoryCategory.value = null
  }
  currentPage.value = 0
  syncQueryToUrl()
  loadProducts()
}

async function loadAccessoryCategories() {
  if (accessoryCategories.value.length > 0) return
  try {
    accessoryCategories.value = await fetchAccessoryCategories()
    accessoryCategoriesError.value = false
  } catch {
    accessoryCategories.value = []
    accessoryCategoriesError.value = true
  }
}

// Apply price filter (triggered by button). The currentQuery change drives the
// useAsyncData refetch; this just resets the page and closes the drawer.
function applyFilters() {
  normalizePriceRange(priceRange.value)
  currentPage.value = 0
  syncQueryToUrl()
  loadProducts()
  showFilters.value = false
}

function openMobileFilters() {
  draftProductType.value = productType.value
  draftCategory.value = selectedCategory.value
  draftCondition.value = selectedCondition.value
  draftAccessoryCategory.value = selectedAccessoryCategory.value
  draftPriceRange.value = [...priceRange.value]
  showFilters.value = true
  nextTick(() => filterDialog.value?.focus())
}

function closeMobileFilters() {
  showFilters.value = false
  nextTick(() => filterTrigger.value?.focus())
}

function selectDraftProductType(type: ProductType) {
  draftProductType.value = type
  if (type === 'ACCESSORY') {
    draftCategory.value = null
    draftCondition.value = null
    loadAccessoryCategories()
  } else {
    draftAccessoryCategory.value = null
  }
}

function clearMobileDraft() {
  draftCategory.value = null
  draftCondition.value = null
  draftAccessoryCategory.value = null
  draftPriceRange.value = [null, null]
}

function applyMobileFilters() {
  normalizePriceRange(draftPriceRange.value)
  productType.value = draftProductType.value
  selectedCategory.value = draftCategory.value
  selectedCondition.value = draftCondition.value
  selectedAccessoryCategory.value = draftAccessoryCategory.value
  priceRange.value = [...draftPriceRange.value]
  currentPage.value = 0
  syncQueryToUrl()
  loadProducts()
  closeMobileFilters()
}

function normalizePriceRange(range: [number | null, number | null]) {
  if (range[0] !== null && range[1] !== null && range[0] > range[1]) {
    [range[0], range[1]] = [range[1], range[0]]
  }
}

watch(showFilters, (open) => {
  if (!import.meta.client) return
  if (open) {
    previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = previousBodyOverflow
  }
})

onBeforeUnmount(() => {
  if (import.meta.client) document.body.style.overflow = previousBodyOverflow
})

function onDialogKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') closeMobileFilters()
  if (event.key !== 'Tab' || !filterDialog.value) return
  const focusable = [...filterDialog.value.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')]
  if (!focusable.length) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
}

const clearFilters = () => {
  selectedCategory.value = null
  selectedCondition.value = null
  selectedAccessoryCategory.value = null
  priceRange.value = [null, null]
  searchQuery.value = ''
  selectedSort.value = 'newest'
  currentPage.value = 0
  syncQueryToUrl()
  loadProducts()
}

const activeFiltersCount = computed(() => {
  let count = 0
  if (isAccessoryMode.value) {
    if (selectedAccessoryCategory.value) count++
  } else {
    if (selectedCategory.value) count++
    if (selectedCondition.value) count++
  }
  if (priceRange.value[0] !== null && priceRange.value[0] > 0) count++
  if (priceRange.value[1] !== null && priceRange.value[1] > 0) count++
  if (searchQuery.value.trim()) count++
  return count
})

function submitSearch() {
  currentPage.value = 0
  syncQueryToUrl()
  loadProducts()
}

function syncQueryToUrl() {
  const query: Record<string, string> = {}
  if (searchQuery.value.trim()) query.q = searchQuery.value.trim()
  if (isAccessoryMode.value) {
    query.type = 'accessory'
    if (selectedAccessoryCategory.value) query.accessory = selectedAccessoryCategory.value
  } else {
    if (selectedCategory.value === ANDROID_GROUP_CODE) {
      query.category = ANDROID_URL_TOKEN
    } else if (selectedCategory.value) {
      query.category = selectedCategory.value
    }
    if (selectedCondition.value) query.condition = selectedCondition.value
  }
  if (selectedSort.value !== 'newest') query.sort = selectedSort.value
  if (priceRange.value[0] !== null && priceRange.value[0] > 0) query.minPrice = String(priceRange.value[0])
  if (priceRange.value[1] !== null && priceRange.value[1] > 0) query.maxPrice = String(priceRange.value[1])
  if (currentPage.value > 0) query.page = String(currentPage.value + 1) // 内部 0-based → URL 1-based
  applyingRouteQuery = true
  router.replace({ query }).finally(() => {
    nextTick(() => { applyingRouteQuery = false })
  })
}

function firstQueryString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

let applyingRouteQuery = false
watch(() => route.query, (query) => {
  if (applyingRouteQuery) return
  applyingRouteQuery = true
  const nextType: ProductType = query.type === 'accessory' ? 'ACCESSORY' : 'DEVICE'
  const nextSort = firstQueryString(query.sort)
  productType.value = nextType
  searchQuery.value = firstQueryString(query.q) ?? ''
  selectedCategory.value = nextType === 'DEVICE'
    ? (query.category === ANDROID_URL_TOKEN ? ANDROID_GROUP_CODE : firstQueryString(query.category) ?? null)
    : null
  selectedAccessoryCategory.value = nextType === 'ACCESSORY' ? firstQueryString(query.accessory) ?? null : null
  selectedCondition.value = nextType === 'DEVICE' && conditions.value.some(c => c.id === query.condition)
    ? firstQueryString(query.condition) ?? null
    : null
  selectedSort.value = nextSort && SORT_VALUES.includes(nextSort as typeof SORT_VALUES[number]) ? nextSort : 'newest'
  const min = Number(firstQueryString(query.minPrice))
  const max = Number(firstQueryString(query.maxPrice))
  priceRange.value = [Number.isFinite(min) && min > 0 ? min : null, Number.isFinite(max) && max > 0 ? max : null]
  const requestedPage = Number(firstQueryString(query.page))
  currentPage.value = Number.isSafeInteger(requestedPage) && requestedPage > 1 ? requestedPage - 1 : 0
  loadProducts()
  nextTick(() => { applyingRouteQuery = false })
}, { deep: true })

// Pagination helpers
function goToPage(page: number) {
  currentPage.value = page
  syncQueryToUrl()
  loadProducts()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const visiblePages = computed(() => {
  const pages: number[] = []
  const total = totalPages.value
  const current = currentPage.value

  if (total <= 7) {
    for (let i = 0; i < total; i++) pages.push(i)
  } else {
    pages.push(0)
    if (current > 3) pages.push(-1) // ellipsis
    const start = Math.max(1, current - 1)
    const end = Math.min(total - 2, current + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (current < total - 4) pages.push(-1) // ellipsis
    pages.push(total - 1)
  }
  return pages
})

onMounted(async () => {
  // If the page hydrated directly into accessory mode (via URL), populate its filter.
  if (isAccessoryMode.value) loadAccessoryCategories()
  // Retry directly from the browser if the SSR environment returned an empty grid
  // without an application error (for example, a temporarily unreachable API route).
  if (!products.value.length && !productsError.value) {
    await loadProducts()
  }
})
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-950">
    <DSContainer class="pt-5">
      <PageHeroBanner
        theme="shop"
        :home-label="t('shopPage.breadcrumb.home')"
        :parent-label="isStableLanding ? t('shopPage.breadcrumb.shop') : ''"
        :parent-to="isStableLanding ? localePath('/shop') : ''"
        :current-label="isStableLanding ? selectedLandingName : t('shopPage.breadcrumb.shop')"
        :home-to="localePath('/')"
        :badge="t('shopPage.badge')"
        :title="landingTitle"
        :subtitle="landingDescription"
        hero-icon="i-lucide-badge-check"
        visual-src="/images/heroes/shop-devices.webp"
        :trust-badges="shopTrustBadges"
      />
      <section
        v-if="isStableLanding"
        class="mt-5 rounded-2xl border border-gray-200 bg-white p-5 text-sm leading-6 text-gray-700 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300"
      >
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          {{ t('shopPage.landing.guideTitle', { category: selectedLandingName }) }}
        </h2>
        <p class="mt-2">{{ t('shopPage.landing.guideBody') }}</p>
      </section>
    </DSContainer>

    <DSContainer class="py-8">
      <div class="flex flex-col lg:flex-row gap-8">
        <!-- Sidebar Filters (Desktop) -->
        <aside class="hidden lg:block w-72 flex-shrink-0">
          <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-6 sticky top-24">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-lg font-bold text-gray-900 dark:text-white">{{ t('shopPage.filters.title') }}</h2>
              <button
                v-if="activeFiltersCount > 0"
                class="text-sm text-brand-600 dark:text-brand-400 hover:underline"
                @click="clearFilters"
              >
                {{ t('shopPage.filters.clearAll') }}
              </button>
            </div>

            <!-- Search Input -->
            <div class="mb-6">
              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  :placeholder="t('header.searchPlaceholder')"
                  class="w-full pl-10 pr-9 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  @keyup.enter="submitSearch"
                />
                <UIcon name="i-lucide-search" class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <button
                  v-if="searchQuery"
                  :aria-label="t('shopPage.filters.clearSearch')"
                  class="absolute right-0 top-1/2 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center text-gray-500 hover:text-gray-700 focus-visible:outline-2 focus-visible:outline-brand-600 dark:hover:text-gray-300"
                  @click="searchQuery = ''; submitSearch()"
                >
                  <UIcon name="i-lucide-x" class="w-4 h-4" />
                </button>
              </div>
            </div>

            <!-- Product Type Toggle (Devices / Accessories) -->
            <div class="mb-6">
              <div
                role="group"
                :aria-label="t('shopPage.filters.productType')"
                class="grid grid-cols-2 gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl"
              >
                <button
                  :aria-pressed="!isAccessoryMode"
                  :class="[
                    'flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-colors',
                    !isAccessoryMode
                      ? 'bg-white dark:bg-gray-900 text-brand-700 dark:text-brand-300 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  ]"
                  @click="selectProductType('DEVICE')"
                >
                  <UIcon name="i-lucide-smartphone" class="w-4 h-4" />
                  {{ t('shopPage.filters.devices') }}
                </button>
                <button
                  :aria-pressed="isAccessoryMode"
                  :class="[
                    'flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-colors',
                    isAccessoryMode
                      ? 'bg-white dark:bg-gray-900 text-brand-700 dark:text-brand-300 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  ]"
                  @click="selectProductType('ACCESSORY')"
                >
                  <UIcon name="i-lucide-cable" class="w-4 h-4" />
                  {{ t('shopPage.filters.accessories') }}
                </button>
              </div>
            </div>

            <!-- Device Category Filter -->
            <div v-if="!isAccessoryMode" class="mb-6">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">{{ t('shopPage.filters.category') }}</h3>
              <p
                v-if="categoriesError"
                class="text-xs text-red-500 dark:text-red-400 mb-2"
              >
                {{ t('shopPage.filters.categoryLoadError') }}
              </p>
              <div class="space-y-2">
                <label class="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <input v-model="selectedCategory" type="radio" :value="null" name="category" class="h-4 w-4 text-brand-600 focus:ring-brand-500">
                  <span class="text-sm text-gray-700 dark:text-gray-300">{{ t('shopPage.filters.allCategories') }}</span>
                </label>
                <label
                  v-for="category in displayCategories"
                  :key="category.id"
                  class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <input
                    type="radio"
                    :value="category.code"
                    :checked="selectedCategory === category.code"
                    name="category"
                    class="w-4 h-4 border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500"
                    @change="selectedCategory = selectedCategory === category.code ? null : category.code"
                  >
                  <UIcon :name="category.icon || DEFAULT_CATEGORY_ICON" class="w-5 h-5 text-gray-400" />
                  <span class="text-sm text-gray-700 dark:text-gray-300 flex-grow">{{ translateTitle(category.nameTranslations, category.name) }}</span>
                </label>
              </div>
            </div>

            <!-- Device condition filter -->
            <div v-if="!isAccessoryMode" class="mb-6">
              <label for="shop-condition" class="mb-3 block text-sm font-semibold text-gray-900 dark:text-white">
                {{ t('shopPage.filters.condition') }}
              </label>
              <select
                id="shop-condition"
                v-model="selectedCondition"
                class="min-h-11 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              >
                <option :value="null">{{ t('shopPage.filters.allConditions') }}</option>
                <option v-for="condition in conditions" :key="condition.id" :value="condition.id">
                  {{ t(condition.labelKey) }}
                </option>
              </select>
              <p v-if="selectedConditionOption" class="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
                {{ t(selectedConditionOption.descKey) }}
              </p>
            </div>

            <!-- Accessory Category Filter -->
            <div v-else class="mb-6">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">{{ t('shopPage.filters.accessoryCategory') }}</h3>
              <p
                v-if="accessoryCategoriesError"
                class="text-xs text-red-500 dark:text-red-400 mb-2"
              >
                {{ t('shopPage.filters.categoryLoadError') }}
              </p>
              <p
                v-else-if="accessoryCategories.length === 0"
                class="text-xs text-gray-400 dark:text-gray-500 mb-2"
              >
                {{ t('shopPage.filters.allAccessories') }}
              </p>
              <div class="space-y-2">
                <label class="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <input v-model="selectedAccessoryCategory" type="radio" :value="null" name="accessory-category" class="h-4 w-4 text-brand-600 focus:ring-brand-500">
                  <span class="text-sm text-gray-700 dark:text-gray-300">{{ t('shopPage.filters.allAccessories') }}</span>
                </label>
                <label
                  v-for="cat in accessoryCategories"
                  :key="cat.code"
                  class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <input
                    type="radio"
                    :value="cat.code"
                    :checked="selectedAccessoryCategory === cat.code"
                    name="accessory-category"
                    class="w-4 h-4 border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500"
                    @change="selectedAccessoryCategory = selectedAccessoryCategory === cat.code ? null : cat.code"
                  >
                  <UIcon :name="cat.icon || 'i-lucide-cable'" class="w-5 h-5 text-gray-400" />
                  <span class="text-sm text-gray-700 dark:text-gray-300 flex-grow">{{ translateTitle(cat.nameTranslations, cat.name) }}</span>
                </label>
              </div>
            </div>

            <!-- Price Range -->
            <div class="mb-6">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">{{ t('shopPage.filters.priceRange') }}</h3>
              <div class="flex items-center gap-3">
                <div class="flex-1">
                  <input
                    v-model.number="priceRange[0]"
                    type="number"
                    :placeholder="t('shopPage.filters.minPrice')"
                    :aria-label="t('shopPage.filters.minPrice')"
                    min="0"
                    class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                </div>
                <span class="text-gray-400">-</span>
                <div class="flex-1">
                  <input
                    v-model.number="priceRange[1]"
                    type="number"
                    :placeholder="t('shopPage.filters.maxPrice')"
                    :aria-label="t('shopPage.filters.maxPrice')"
                    min="0"
                    class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                </div>
              </div>
            </div>

            <!-- Apply Button -->
            <button
              class="w-full bg-brand-600 hover:bg-brand-700 text-white py-3 px-6 rounded-xl font-medium transition-colors"
              @click="applyFilters"
            >
              {{ t('shopPage.filters.apply') }}
            </button>
          </div>
        </aside>

        <!-- Main Content -->
        <section class="flex-1" :aria-busy="loading">
          <!-- Results Header -->
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div class="flex items-center gap-4">
              <!-- Mobile Filter Button -->
              <button
                ref="filterTrigger"
                class="lg:hidden flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300"
                aria-haspopup="dialog"
                :aria-expanded="showFilters"
                @click="openMobileFilters"
              >
                <UIcon name="i-lucide-sliders-horizontal" class="w-5 h-5" />
                <span>{{ t('shopPage.filters.title') }}</span>
                <span v-if="activeFiltersCount > 0" class="bg-brand-600 text-white text-xs px-2 py-0.5 rounded-full">
                  {{ activeFiltersCount }}
                </span>
              </button>

              <p aria-live="polite" class="text-sm text-gray-600 dark:text-gray-400">
                {{ t('shopPage.results.productsFound', { count: totalProducts }) }}
              </p>
            </div>

            <div class="flex items-center gap-3">
              <label for="shop-sort" class="text-sm text-gray-500 dark:text-gray-400">{{ t('shopPage.sort.label') }}</label>
              <select
                id="shop-sort"
                v-model="selectedSort"
                class="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option
                  v-for="option in sortOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Loading Skeleton -->
          <div v-if="loading" aria-hidden="true" class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            <div
              v-for="i in 6"
              :key="i"
              class="bg-white dark:bg-gray-900 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 animate-pulse"
            >
              <div class="aspect-square bg-gray-200 dark:bg-gray-800" />
              <div class="p-4 space-y-3">
                <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                <div class="h-5 bg-gray-200 dark:bg-gray-700 rounded w-24 mt-4" />
                <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full" />
              </div>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="productsError" role="alert" class="rounded-2xl border border-red-200 bg-red-50 p-8 text-center dark:border-red-900 dark:bg-red-950/30">
            <p class="text-red-700 dark:text-red-300">{{ t('shopPage.results.loadError') }}</p>
            <button class="mt-4 min-h-11 rounded-xl bg-brand-600 px-5 py-2 text-white" @click="loadProducts">{{ t('shopPage.results.retry') }}</button>
          </div>

          <!-- Empty State -->
          <div v-else-if="products.length === 0" class="text-center py-20">
            <UIcon
              :name="isAccessoryMode ? 'i-lucide-cable' : 'i-lucide-search-x'"
              class="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4"
            />
            <p class="text-gray-500 dark:text-gray-400 text-lg">
              {{ isAccessoryMode ? t('shopPage.results.noAccessories') : t('shopPage.results.noProducts') }}
            </p>
            <p v-if="isAccessoryMode" class="text-gray-400 dark:text-gray-500 text-sm mt-1">
              {{ t('shopPage.results.noAccessoriesHint') }}
            </p>
            <button
              v-if="activeFiltersCount > 0"
              class="mt-4 text-brand-600 dark:text-brand-400 hover:underline font-medium"
              @click="clearFilters"
            >
              {{ t('shopPage.filters.clearAll') }}
            </button>
          </div>

          <!-- Product Grid -->
          <div v-else class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            <ProductCard
              v-for="product in products"
              :key="product.id"
              :product="product"
            />
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="flex justify-center items-center gap-2 mt-12">
            <button
              :disabled="currentPage === 0"
              :aria-label="t('shopPage.pagination.previous')"
              :class="[
                  'w-11 h-11 flex items-center justify-center rounded-xl border transition-colors',
                currentPage === 0
                  ? 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-brand-500'
              ]"
              @click="goToPage(currentPage - 1)"
            >
              <UIcon name="i-lucide-chevron-left" class="w-5 h-5" />
            </button>

            <template v-for="(page, idx) in visiblePages" :key="idx">
              <span v-if="page === -1" class="text-gray-400 px-2">...</span>
              <button
                v-else
                :class="[
                  'w-11 h-11 flex items-center justify-center rounded-xl font-medium transition-colors',
                  page === currentPage
                    ? 'bg-brand-600 text-white'
                    : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-brand-500'
                ]"
                :aria-current="page === currentPage ? 'page' : undefined"
                :aria-label="String(page + 1)"
                @click="goToPage(page)"
              >
                {{ page + 1 }}
              </button>
            </template>

            <button
              :disabled="currentPage >= totalPages - 1"
              :aria-label="t('shopPage.pagination.next')"
              :class="[
                  'w-11 h-11 flex items-center justify-center rounded-xl border transition-colors',
                currentPage >= totalPages - 1
                  ? 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-brand-500'
              ]"
              @click="goToPage(currentPage + 1)"
            >
              <UIcon name="i-lucide-chevron-right" class="w-5 h-5" />
            </button>
          </div>
        </section>
      </div>
    </DSContainer>

    <!-- Mobile Filter Drawer -->
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="showFilters"
          class="fixed inset-0 bg-black/50 z-50 lg:hidden"
          aria-hidden="true"
          @click="closeMobileFilters"
        />
      </Transition>
      <Transition name="slide-up">
        <div
          v-if="showFilters"
          ref="filterDialog"
          role="dialog"
          aria-modal="true"
          :aria-label="t('shopPage.filters.title')"
          tabindex="-1"
          class="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 rounded-t-3xl z-50 lg:hidden max-h-[85dvh] overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)] outline-none"
          @keydown="onDialogKeydown"
        >
          <div class="p-6">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-lg font-bold text-gray-900 dark:text-white">{{ t('shopPage.filters.title') }}</h2>
              <button class="min-h-11 min-w-11 rounded-lg" :aria-label="t('shopPage.filters.close')" @click="closeMobileFilters">
                <UIcon name="i-lucide-x" class="w-6 h-6 text-gray-500" />
              </button>
            </div>

            <!-- Product Type Toggle (Devices / Accessories) -->
            <div class="mb-6">
              <div
                role="group"
                :aria-label="t('shopPage.filters.productType')"
                class="grid grid-cols-2 gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl"
              >
                <button
                  :aria-pressed="draftProductType === 'DEVICE'"
                  :class="[
                    'flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-colors',
                    draftProductType === 'DEVICE'
                      ? 'bg-white dark:bg-gray-900 text-brand-700 dark:text-brand-300 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400'
                  ]"
                  @click="selectDraftProductType('DEVICE')"
                >
                  <UIcon name="i-lucide-smartphone" class="w-4 h-4" />
                  {{ t('shopPage.filters.devices') }}
                </button>
                <button
                  :aria-pressed="draftProductType === 'ACCESSORY'"
                  :class="[
                    'flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-colors',
                    draftProductType === 'ACCESSORY'
                      ? 'bg-white dark:bg-gray-900 text-brand-700 dark:text-brand-300 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400'
                  ]"
                  @click="selectDraftProductType('ACCESSORY')"
                >
                  <UIcon name="i-lucide-cable" class="w-4 h-4" />
                  {{ t('shopPage.filters.accessories') }}
                </button>
              </div>
            </div>

            <!-- Device Category Filter -->
            <div v-if="draftProductType === 'DEVICE'" class="mb-6">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">{{ t('shopPage.filters.category') }}</h3>
              <p
                v-if="categoriesError"
                class="text-xs text-red-500 dark:text-red-400 mb-2"
              >
                {{ t('shopPage.filters.categoryLoadError') }}
              </p>
              <div class="flex flex-wrap gap-2">
                <button type="button" :aria-pressed="draftCategory === null" :class="['min-h-11 rounded-full px-4 py-2 text-sm font-medium', draftCategory === null ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300']" @click="draftCategory = null">
                  {{ t('shopPage.filters.allCategories') }}
                </button>
                <button
                  v-for="category in displayCategories"
                  :key="category.id"
                  :class="[
                    'min-h-11 px-4 py-2 rounded-full text-sm font-medium transition-colors',
                    draftCategory === category.code
                      ? 'bg-brand-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  ]"
                  @click="draftCategory = draftCategory === category.code ? null : category.code"
                >
                  {{ translateTitle(category.nameTranslations, category.name) }}
                </button>
              </div>
            </div>

            <!-- Accessory Category Filter -->
            <div v-else class="mb-6">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">{{ t('shopPage.filters.accessoryCategory') }}</h3>
              <p
                v-if="accessoryCategoriesError"
                class="text-xs text-red-500 dark:text-red-400 mb-2"
              >
                {{ t('shopPage.filters.categoryLoadError') }}
              </p>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="cat in accessoryCategories"
                  :key="cat.code"
                  :class="[
                    'min-h-11 px-4 py-2 rounded-full text-sm font-medium transition-colors',
                    draftAccessoryCategory === cat.code
                      ? 'bg-brand-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  ]"
                  @click="draftAccessoryCategory = draftAccessoryCategory === cat.code ? null : cat.code"
                >
                  {{ translateTitle(cat.nameTranslations, cat.name) }}
                </button>
              </div>
            </div>

            <fieldset v-if="draftProductType === 'DEVICE'" class="mb-6">
              <legend class="text-sm font-semibold text-gray-900 dark:text-white mb-3">{{ t('shopPage.filters.condition') }}</legend>
              <div class="grid grid-cols-2 gap-2">
                <button v-for="condition in conditions" :key="condition.id" type="button" :aria-pressed="draftCondition === condition.id" :class="['min-h-11 rounded-full px-3 py-2 text-sm', draftCondition === condition.id ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300']" @click="draftCondition = draftCondition === condition.id ? null : condition.id">
                  {{ t(condition.labelKey) }}
                </button>
              </div>
            </fieldset>

            <!-- Price Range -->
            <div class="mb-6">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">{{ t('shopPage.filters.priceRange') }}</h3>
              <div class="flex items-center gap-3">
                <div class="flex-1">
                  <input
                    v-model.number="draftPriceRange[0]"
                    type="number"
                    :placeholder="t('shopPage.filters.minPrice')"
                    :aria-label="t('shopPage.filters.minPrice')"
                    min="0"
                    class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                </div>
                <span class="text-gray-400">-</span>
                <div class="flex-1">
                  <input
                    v-model.number="draftPriceRange[1]"
                    type="number"
                    :placeholder="t('shopPage.filters.maxPrice')"
                    :aria-label="t('shopPage.filters.maxPrice')"
                    min="0"
                    class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="sticky bottom-0 -mx-6 mt-6 flex gap-3 border-t border-gray-200 bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-[0_-8px_20px_rgba(0,0,0,0.06)] dark:border-gray-800 dark:bg-gray-900">
              <button
                class="flex-1 py-3 px-6 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 font-medium"
                @click="clearMobileDraft"
              >
                {{ t('shopPage.filters.clearAll') }}
              </button>
              <button
                class="flex-1 py-3 px-6 bg-brand-600 text-white rounded-xl font-medium"
                @click="applyMobileFilters"
              >
                {{ t('shopPage.filters.showResults') }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active,
  .slide-up-enter-active,
  .slide-up-leave-active {
    transition-duration: 0.01ms;
  }
}
</style>
